import { useEffect, useRef } from 'react';
import './exploration.css';

import clsx from 'clsx';

import BubbleCanvas from './components/BubbleCanvas';
import CoverageCanvas from './components/CoverageCanvas/CoverageCanvas';
import TreemapCanvas from './components/TreemapCanvas/TreemapCanvas';
import TreeCanvas from './components/TreeCanvas/TreeCanvas';
import ExplorationCanvasTopChrome from './components/ExplorationCanvasTopChrome';
import { explorationCanvasWrapId } from './components/explorationCanvasChrome';
import ExplorationLoadingOverlay from './components/ExplorationLoadingOverlay';
import ExplorationSidebar from './components/ExplorationSidebar';
import ExplorationTopBar from './components/ExplorationTopBar';
import { ExplorationRootProvider } from './context/ExplorationDataProvider';
import { useExplorationData, useExplorationActions, useExplorationState, useFilteredVolumes } from './context/ExplorationContext';
import { useExplorationUrlSync } from './hooks/useExplorationUrlSync';
import { useInert } from './hooks/useInert';
import { emptyDrillLocation, isDrillLocationEmpty } from './lib/drill/drillLocation';
import Button from '~/shared/components/ButtonsV2';
import Typography from '~/shared/components/Typography';

function ExplorationErrorBanner() {
  const { error, refresh, loading } = useExplorationData();
  if (!error || loading) return null;

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2 bg-light-red border-b border-red row-start-2">
      <Typography variant="p3" color="text-red">
        Failed to load volumes: {error}
      </Typography>
      <Button type="button" variant="third" size="sm" onClick={refresh}>
        <Typography variant="buttons-m" fontWeight={500}>
          Retry
        </Typography>
      </Button>
    </div>
  );
}

function ExplorationLayout() {
  useExplorationUrlSync();
  const state = useExplorationState();
  const actions = useExplorationActions();
  const filtered = useFilteredVolumes();
  const { initialLoading } = useExplorationData();
  const sidebarRef = useRef<HTMLElement>(null);
  const inCoverage = state.view === 'coverage';
  const inExplorationOrActivation = state.view === 'exploration' || state.view === 'activation';
  const showBreadcrumb =
    inExplorationOrActivation && state.displayMode === 'bubbles' && filtered.length > 0;
  const mountSidebar = !initialLoading;
  const sidebarOpen = !state.sidebarCollapsed;

  useInert(sidebarRef, !sidebarOpen);

  useEffect(() => {
    if (sidebarOpen) return;
    const aside = sidebarRef.current;
    if (aside?.contains(document.activeElement)) {
      document.getElementById(explorationCanvasWrapId)?.focus();
    }
  }, [sidebarOpen]);
  useEffect(() => {
    const sel = state.selectedNode;
    if (!sel) return;

    const selectionStillVisible =
      sel.kind === 'volume'
        ? filtered.some((v) => v.ebsId === sel.volume.ebsId)
        : filtered.some((v) => v.instanceId === sel.instanceId);

    if (!selectionStillVisible) {
      actions.clearSelection();
    }
  }, [filtered, state.selectedNode, actions]);

  useEffect(() => {
    if (filtered.length > 0 || isDrillLocationEmpty(state.drillPath)) return;
    actions.setDrillPath(emptyDrillLocation());
  }, [filtered.length, state.drillPath, actions]);

  return (
    <div className="grid h-full min-h-0 min-w-0 overflow-hidden bg-bg grid-rows-[auto_auto_minmax(0,1fr)]">
      <ExplorationTopBar />
      <ExplorationErrorBanner />
      <div className="relative flex min-h-0 overflow-hidden row-start-3">
        {mountSidebar && (
          <aside
            ref={sidebarRef}
            aria-hidden={!sidebarOpen}
            className={clsx(
              'exploration-sidebar side-panel-scrollbar bg-white min-h-0 shrink-0 overflow-x-hidden overflow-y-auto',
              'transition-[width] duration-300 ease-in-out motion-reduce:transition-none',
              sidebarOpen
                ? 'w-[445px] border-r border-primary-70'
                : 'w-0 pointer-events-none'
            )}
          >
            <div
              className={clsx(
                'exploration-sidebar-body w-full min-w-0 min-h-full transform transition-transform duration-300 ease-in-out motion-reduce:transition-none',
                sidebarOpen ? 'translate-x-0' : '-translate-x-full'
              )}
            >
              <ExplorationSidebar />
            </div>
          </aside>
        )}
        <div
          id={explorationCanvasWrapId}
          role="tabpanel"
          tabIndex={-1}
          className="exploration-canvas-wrap min-h-0 min-w-0 flex-1 outline-none"
        >
          <ExplorationCanvasTopChrome showBreadcrumb={showBreadcrumb} />
          {inCoverage ? (
            <CoverageCanvas />
          ) : state.displayMode === 'treemap' ? (
            <TreemapCanvas />
          ) : state.displayMode === 'tree' ? (
            <TreeCanvas />
          ) : (
            <BubbleCanvas />
          )}
          <ExplorationLoadingOverlay />
        </div>
      </div>
      <div id="exploration-tooltip" className="exploration-tooltip" role="tooltip" aria-hidden="true" />
    </div>
  );
}

export default function ExplorationPage() {
  return (
    <ExplorationRootProvider>
      <ExplorationLayout />
    </ExplorationRootProvider>
  );
}
